import { PositionUpdate } from './types';

/**
 * Node with position and dimensions
 */
export interface NodeWithPosition {
    id: number;
    position: { x: number; y: number };
    width?: number;
    height?: number;
}

/**
 * Calculates adjusted positions while preserving the center point of the affected nodes.
 * This ensures that when a group of nodes is repositioned, their collective center
 * remains in the same location.
 *
 * @param positionUpdates - Array of position updates to apply
 * @param nodes - Array of nodes to reference
 * @param defaultWidth - Default width for nodes without explicit width (default: 200)
 * @param defaultHeight - Default height for nodes without explicit height (default: 100)
 * @returns Map of node IDs to adjusted positions
 */
export function calculateCenterPreservingPositions(
    positionUpdates: PositionUpdate[],
    nodes: NodeWithPosition[],
    defaultWidth: number = 200,
    defaultHeight: number = 100,
): Map<number, { x: number; y: number }> {
    if (positionUpdates.length === 0) {
        return new Map();
    }

    // Calculate center of original positions for affected nodes
    const affectedNodeIds = new Set(positionUpdates.map(u => u.nodeId));
    const affectedNodes = nodes.filter(n => affectedNodeIds.has(n.id));

    let sumX = 0;
    let sumY = 0;
    for (const node of affectedNodes) {
        const width = node.width || defaultWidth;
        const height = node.height || defaultHeight;
        sumX += node.position.x + width / 2;
        sumY += node.position.y + height / 2;
    }
    const originalCenterX = sumX / affectedNodes.length;
    const originalCenterY = sumY / affectedNodes.length;

    // Calculate center of new positions
    sumX = 0;
    sumY = 0;
    for (const update of positionUpdates) {
        const node = nodes.find(n => n.id === update.nodeId)!;
        const width = node.width || defaultWidth;
        const height = node.height || defaultHeight;
        sumX += update.x + width / 2;
        sumY += update.y + height / 2;
    }
    const newCenterX = sumX / positionUpdates.length;
    const newCenterY = sumY / positionUpdates.length;

    // Calculate offset to maintain original center
    const offsetX = originalCenterX - newCenterX;
    const offsetY = originalCenterY - newCenterY;

    // Create map of new positions with offset applied
    return new Map(positionUpdates.map(update => [update.nodeId, { x: update.x + offsetX, y: update.y + offsetY }]));
}

/**
 * Applies position updates to nodes while preserving the center point of the affected nodes.
 * This ensures that when a group of nodes is repositioned, their collective center
 * remains in the same location.
 *
 * @param positionUpdates - Array of position updates to apply
 * @param nodes - Array of nodes to update
 * @param defaultWidth - Default width for nodes without explicit width (default: 200)
 * @param defaultHeight - Default height for nodes without explicit height (default: 100)
 * @returns Updated array of nodes with adjusted positions
 */
export function applyPositionUpdatesWithCenterPreservation<T extends NodeWithPosition>(
    positionUpdates: PositionUpdate[],
    nodes: T[],
    defaultWidth: number = 200,
    defaultHeight: number = 100,
): T[] {
    if (positionUpdates.length === 0) {
        return nodes;
    }

    const newPositions = calculateCenterPreservingPositions(positionUpdates, nodes, defaultWidth, defaultHeight);

    // Apply position updates to nodes
    return nodes.map(node => {
        const newPos = newPositions.get(node.id);
        if (newPos) {
            return { ...node, position: newPos };
        }
        return node;
    });
}
